import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table, Input, Row, Col, Label } from 'reactstrap';
import { JhiItemCount, JhiPagination, TextFormat, Translate, translate, byteSize, getPaginationState, openFile } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp, faPrint, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { library } from '@fortawesome/fontawesome-svg-core';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import { getEntities } from './foreigner-residence-report.reducer';

export const ForeignerResidenceReport = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();
  const [hasSearched, setHasSearched] = useState(false);

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'recordDate'), pageLocation.search),
  );
  const [filters, setFilters] = useState({
    fromRecordDate: '',
    toRecordDate: '',
  });

  const foreignerResidenceList = useAppSelector(state => state.foreignerResidence.entities);
  const loading = useAppSelector(state => state.foreignerResidence.loading);
  const totalItems = useAppSelector(state => state.foreignerResidence.totalItems);
  library.add(faPrint);

  const handleFilterChange = e => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleSearch = () => {
    if (filters.fromRecordDate && filters.toRecordDate) {
      setPaginationState({ ...paginationState, activePage: 1 });
      setHasSearched(true);
    } else {
      setHasSearched(false);
    }
  };

  const handleExportToExcel = () => {
    if (!filters.fromRecordDate || !filters.toRecordDate || !foreignerResidenceList || foreignerResidenceList.length === 0) return;
    const exportData = foreignerResidenceList.map(item => ({
      [translate('foreignerResidenceApp.foreignerResidence.recordNumber')]: item.recordNumber,
      [translate('foreignerResidenceApp.foreignerResidence.createDate')]: item.createDate
        ? new Date(item.createDate).toLocaleDateString()
        : '',
      [translate('foreignerResidenceApp.foreignerResidence.recordDate')]: item.recordDate
        ? new Date(item.recordDate).toLocaleDateString()
        : '',
      [translate('foreignerResidenceApp.foreignerResidence.foreignerFullName')]: (() => {
        return [item.foreignerTitle, item.foreignerFullName].filter(Boolean).join(' ').toUpperCase();
      })(),
      [translate('foreignerResidenceApp.foreignerResidence.nationality')]: (() => {
        const nationalityKey = item.nationality ?? '';
        const translatedNationality = nationalityKey ? translate(`foreignerResidenceApp.Nationality.${nationalityKey}`) : '';
        return translatedNationality.replace(/^[^)]*\)\s*/, '');
      })(),
      [translate('foreignerResidenceApp.foreignerResidence.foreignerPassportNo')]: item.foreignerPassportNo,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'ForeignerResidences');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    const today = new Date().toISOString().slice(0, 10); // Format: YYYY-MM-DD
    saveAs(blob, `foreigner_residence_${today}.xlsx`);
  };

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        fromRecordDate: filters.fromRecordDate,
        toRecordDate: filters.toRecordDate,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    const anyFilter = filters.fromRecordDate && filters.toRecordDate;
    if (anyFilter) {
      getAllEntities();
    }
  }, [hasSearched, paginationState.activePage, paginationState.order, paginationState.sort, filters.fromRecordDate, filters.toRecordDate]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="foreigner-residence-heading" data-cy="ForeignerResidenceHeading">
        <Translate contentKey="foreignerResidenceApp.foreignerResidence.home.title">Foreigner Residences</Translate>
        <div className="d-flex justify-content-end">
          <Button
            className="me-2"
            color="success"
            disabled={!(filters.fromRecordDate && filters.toRecordDate)}
            onClick={handleExportToExcel}
          >
            <FontAwesomeIcon icon={faFileExcel} className="me-2" />{' '}
            <Translate contentKey="foreignerResidenceApp.foreignerResidence.home.excelExportLabel">สร้างรายงานไฟล์</Translate>
          </Button>
        </div>
      </h2>
      <Row className="mb-3 align-items-end">
        <Col md="4">
          <Label for="fromRecordDate">
            จาก <Translate contentKey="foreignerResidenceApp.foreignerResidence.recordDate">Record Date</Translate>
          </Label>
          <Input
            type="date"
            name="fromRecordDate"
            value={filters.fromRecordDate || ''}
            onChange={handleFilterChange}
            placeholder="From Record Date"
          />
        </Col>
        <Col md="4">
          <Label for="toRecordDate">
            ถึง <Translate contentKey="foreignerResidenceApp.foreignerResidence.recordDate">Record Date</Translate>
          </Label>
          <Input
            type="date"
            name="toRecordDate"
            value={filters.toRecordDate || ''}
            onChange={handleFilterChange}
            placeholder="To Record Date"
          />
        </Col>
        <Col md="4" className="text-end">
          <Button color="primary" onClick={handleSearch}>
            <Translate contentKey="entity.action.search">Search</Translate>
          </Button>
        </Col>
      </Row>
      <div className="table-responsive">
        {filters.fromRecordDate && filters.toRecordDate && foreignerResidenceList && foreignerResidenceList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('recordNumber')}>
                  <Translate contentKey="foreignerResidenceApp.foreignerResidence.recordNumber">Record Number</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('recordNumber')} />
                </th>
                <th className="hand" onClick={sort('createDate')}>
                  <Translate contentKey="foreignerResidenceApp.foreignerResidence.createDate">Create Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createDate')} />
                </th>
                <th className="hand" onClick={sort('recordDate')}>
                  <Translate contentKey="foreignerResidenceApp.foreignerResidence.recordDate">Record Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('recordDate')} />
                </th>
                <th className="hand" onClick={sort('foreignerFullName')}>
                  <Translate contentKey="foreignerResidenceApp.foreignerResidence.foreignerFullName">Foreigner Full Name</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('foreignerFullName')} />
                </th>
                <th className="hand" onClick={sort('nationality')}>
                  <Translate contentKey="foreignerResidenceApp.foreignerResidence.nationality">Nationality</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('nationality')} />
                </th>
                <th className="hand" onClick={sort('foreignerPassportNo')}>
                  <Translate contentKey="foreignerResidenceApp.foreignerResidence.foreignerPassportNo">Foreigner Passport No</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('foreignerPassportNo')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {foreignerResidenceList.map((foreignerResidence, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/foreigner-residence/${foreignerResidence.id}/edit`} color="link" size="sm">
                      {foreignerResidence.recordNumber}
                    </Button>
                  </td>
                  <td>
                    {foreignerResidence.createDate ? (
                      <TextFormat type="date" value={foreignerResidence.createDate} format={APP_LOCAL_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {foreignerResidence.recordDate ? (
                      <TextFormat type="date" value={foreignerResidence.recordDate} format={APP_LOCAL_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {[foreignerResidence.foreignerTitle, foreignerResidence.foreignerFullName].filter(Boolean).join(' ').toUpperCase()}
                  </td>
                  <td>
                    <Translate contentKey={`foreignerResidenceApp.Nationality.${foreignerResidence.nationality}`} />
                  </td>
                  <td>{foreignerResidence.foreignerPassportNo}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="foreignerResidenceApp.foreignerResidence.home.notFound">No Foreigner Residences found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div
          className={
            filters.fromRecordDate && filters.toRecordDate && foreignerResidenceList && foreignerResidenceList.length > 0 ? '' : 'd-none'
          }
        >
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default ForeignerResidenceReport;
